
import React, { useState, useEffect } from 'react';
import AddItemModal from '../../components/AddItemModal';
import SortableDropdownList from '../../components/SortableDropdownList';
import { addDropdownOption, deleteDropdownOption, reorderDropdownOptions, renameDropdownOption } from '../../services/apiService';

const AdminSettingsPage = ({ options, onUpdate }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState(null);

    useEffect(() => {
        if (modalConfig) {
            setIsModalOpen(true);
        }
    }, [modalConfig]);


    const closeModal = () => {
        setIsModalOpen(false);
        setModalConfig(null);
    };

    const openAddModal = (key, subkey, title) => {
        setModalConfig({ key, subkey, title });
    };

    const handleSave = async (formData) => {
        const { key, subkey } = modalConfig;
        const value = formData.itemName;
        if (value && value.trim()) {
            try {
                let optionType;
                if (key === 'salesMessageNames') {
                    optionType = 'salesMessageNames';
                } else if (key === 'serviceMessageNames') {
                    optionType = 'serviceMessageNames';
                } else if (key === 'feeds') {
                    optionType = 'feeds';
                } else if (key === 'sendWhen' && subkey === 'events') {
                    optionType = 'sendWhenEvents';
                } else if (key === 'checklistCategories') {
                    optionType = 'checklistCategories';
                }
                
                if (optionType) {
                    await addDropdownOption(optionType, value);
                    
                    // Update local state by refetching data
                    const updatedOptions = JSON.parse(JSON.stringify(options));
                    if (subkey) {
                        updatedOptions[key][subkey].push(value);
                    } else {
                        updatedOptions[key].push(value);
                    }
                    onUpdate(updatedOptions);
                    
                    closeModal();
                }
            } catch (error) {
                console.error("Error adding dropdown option:", error);
                alert("Error adding option. Please try again.");
            }
        }
    };

    const handleDelete = async (key, value, subkey = null) => {
        try {
            let optionType;
            if (key === 'salesMessageNames') {
                optionType = 'salesMessageNames';
            } else if (key === 'serviceMessageNames') {
                optionType = 'serviceMessageNames';
            } else if (key === 'feeds') {
                optionType = 'feeds';
            } else if (key === 'sendWhen' && subkey === 'events') {
                optionType = 'sendWhenEvents';
            } else if (key === 'checklistCategories') {
                optionType = 'checklistCategories';
            }

            if (optionType) {
                await deleteDropdownOption(optionType, value);

                // Update local state
                const newOptions = JSON.parse(JSON.stringify(options));
                if (subkey) {
                    newOptions[key][subkey] = newOptions[key][subkey].filter(item => item !== value);
                } else {
                    newOptions[key] = newOptions[key].filter(item => item !== value);
                }
                onUpdate(newOptions);
            }
        } catch (error) {
            console.error("Error deleting dropdown option:", error);
            alert("Error deleting option. Please try again.");
        }
    };

    const handleReorder = async (key, newOrder, subkey = null) => {
        try {
            let optionType;
            if (key === 'salesMessageNames') {
                optionType = 'salesMessageNames';
            } else if (key === 'serviceMessageNames') {
                optionType = 'serviceMessageNames';
            } else if (key === 'feeds') {
                optionType = 'feeds';
            } else if (key === 'sendWhen' && subkey === 'events') {
                optionType = 'sendWhenEvents';
            } else if (key === 'checklistCategories') {
                optionType = 'checklistCategories';
            }

            if (optionType) {
                await reorderDropdownOptions(optionType, newOrder);

                // Update local state
                const newOptions = JSON.parse(JSON.stringify(options));
                if (subkey) {
                    newOptions[key][subkey] = newOrder;
                } else {
                    newOptions[key] = newOrder;
                }
                onUpdate(newOptions);
            }
        } catch (error) {
            console.error("Error reordering dropdown options:", error);
            alert("Error reordering options. Please try again.");
        }
    };

    const handleRename = async (key, oldValue, newValue, subkey = null) => {
        try {
            let optionType;
            if (key === 'salesMessageNames') {
                optionType = 'salesMessageNames';
            } else if (key === 'serviceMessageNames') {
                optionType = 'serviceMessageNames';
            } else if (key === 'feeds') {
                optionType = 'feeds';
            } else if (key === 'sendWhen' && subkey === 'events') {
                optionType = 'sendWhenEvents';
            } else if (key === 'checklistCategories') {
                optionType = 'checklistCategories';
            }

            if (optionType) {
                await renameDropdownOption(optionType, oldValue, newValue);

                // Update local state
                const newOptions = JSON.parse(JSON.stringify(options));
                if (subkey) {
                    const index = newOptions[key][subkey].indexOf(oldValue);
                    if (index !== -1) {
                        newOptions[key][subkey][index] = newValue;
                    }
                } else {
                    const index = newOptions[key].indexOf(oldValue);
                    if (index !== -1) {
                        newOptions[key][index] = newValue;
                    }
                }
                onUpdate(newOptions);
            }
        } catch (error) {
            console.error("Error renaming dropdown option:", error);
            alert(`Error renaming option: ${error.message}`);
        }
    };



    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Manage Dropdown Options</h2>
            <div className="mb-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <strong>Drag and drop</strong> items to reorder them. <strong>Click the edit icon</strong> to rename items. Changes are saved automatically.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <SortableDropdownList
                    data={options.salesMessageNames || []}
                    title="Sales Message Names"
                    onDelete={(value) => handleDelete('salesMessageNames', value)}
                    onReorder={(newOrder) => handleReorder('salesMessageNames', newOrder)}
                    onRename={(oldValue, newValue) => handleRename('salesMessageNames', oldValue, newValue)}
                    onAdd={() => openAddModal('salesMessageNames', null, 'Add New Sales Message Names')}
                />
                <SortableDropdownList
                    data={options.serviceMessageNames || []}
                    title="Service Message Names"
                    onDelete={(value) => handleDelete('serviceMessageNames', value)}
                    onReorder={(newOrder) => handleReorder('serviceMessageNames', newOrder)}
                    onRename={(oldValue, newValue) => handleRename('serviceMessageNames', oldValue, newValue)}
                    onAdd={() => openAddModal('serviceMessageNames', null, 'Add New Service Message Names')}
                />
                <SortableDropdownList
                    data={options.feeds || []}
                    title="Feeds"
                    onDelete={(value) => handleDelete('feeds', value)}
                    onReorder={(newOrder) => handleReorder('feeds', newOrder)}
                    onRename={(oldValue, newValue) => handleRename('feeds', oldValue, newValue)}
                    onAdd={() => openAddModal('feeds', null, 'Add New Feeds')}
                />
                <SortableDropdownList
                    data={options.sendWhen?.events || []}
                    title="Send When Events"
                    onDelete={(value) => handleDelete('sendWhen', value, 'events')}
                    onReorder={(newOrder) => handleReorder('sendWhen', newOrder, 'events')}
                    onRename={(oldValue, newValue) => handleRename('sendWhen', oldValue, newValue, 'events')}
                    onAdd={() => openAddModal('sendWhen', 'events', 'Add New Send When Events')}
                />
                <SortableDropdownList
                    data={options.checklistCategories || []}
                    title="Checklist Categories"
                    onDelete={(value) => handleDelete('checklistCategories', value)}
                    onReorder={(newOrder) => handleReorder('checklistCategories', newOrder)}
                    onRename={(oldValue, newValue) => handleRename('checklistCategories', oldValue, newValue)}
                    onAdd={() => openAddModal('checklistCategories', null, 'Add New Checklist Category')}
                />
            </div>


            {modalConfig && <AddItemModal isOpen={isModalOpen} onClose={closeModal} onSave={handleSave} title={modalConfig.title} fields={[{ name: 'Name', id: 'itemName' }]} />}
        </div>
    );
};

export default AdminSettingsPage;
